import React, { useRef, useState, useEffect } from 'react';

import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';

import { preparationSectionTranslationKey } from '../../../utils';
import { useAppDispatch } from '../../../app/hooks';
import { clearFilter } from '../../../features/filter/filterSlice';
import useTranslation from '../../../features/translation/hooks/useTranslation';
import { econdaClickEvent } from '../../../Econda';

enum RoutePath {
  RESULTS = 'results',
  ENTRY_PAGE = '',
}

enum TranslationKeys {
  HOME_PAGE = 'headlineName',
  RESULTS_PAGE = 'resultsPage',
  VERTICAL_MOTION = 'verticalMotion',
  HORIZONTAL_MOTION = 'horizontalMotion',
  INCLINE_MOTION = 'inclineMotion',
  ROTATORY_MOVEMENT = 'rotatoryMovement',
}

const useHeadline = () => {
  const dispatch = useAppDispatch();
  const t = useTranslation();

  const [isResultPage, setIsResultPage] = useState(false);
  const [headlineName, setHeadlineName] = useState('');
  const [isFirstPage, setIsFirstPage] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const pathArray = pathname.split('/');
    const lastPath = pathArray[pathArray.length - 1];
    const motionPath = pathArray[1];
    const isResultPage = lastPath === RoutePath.RESULTS;
    const isEntryPage = lastPath === RoutePath.ENTRY_PAGE;

    setIsResultPage(isResultPage);
    setIsFirstPage(isEntryPage);

    if (lastPath === '') {
      dispatch(clearFilter());
    }

    switch (lastPath) {
      case RoutePath.ENTRY_PAGE:
        setHeadlineName(t(TranslationKeys.HOME_PAGE));
        return;
      default:
        if (motionPath) {
          const sectionName = preparationSectionTranslationKey(motionPath) as string;

          type TR_KEYS = keyof typeof TranslationKeys;
          // TIP: select motion group (vertical, horizontal...) and translate the word using the motonCase as key of object TranslationKeys
          let motionCase = motionPath.split('_')[0].replace('-', '_').toLocaleUpperCase();
          if (motionCase === 'ROTATIONAL_MOTION') motionCase = 'ROTATORY_MOVEMENT';

          const sectionHeadline = `${t(TranslationKeys[motionCase as TR_KEYS])} - ${t(sectionName)}`;

          setHeadlineName(t(sectionHeadline));
        }
    }
  }, [dispatch, isFirstPage, pathname, t]);

  const resetApplication = (e: React.MouseEvent) => {
    e.preventDefault();
    econdaClickEvent(pathname, 'back button modal', 'reset application', 'modal');
    setModalOpen(true);
  };

  const goBack = () => {
    const pathArray = pathname.split('/');
    const motionPath = pathArray[1];
    let econdaObject;

    if (isResultPage) {
      econdaObject = { previousPage: 'parameters page', currentPage: 'results page' };
      navigate(`/${motionPath}`);
    } else if (!isResultPage && !isFirstPage) {
      econdaObject = { previousPage: 'results page', currentPage: 'parameters page' };
      navigate(`/`);
    } else {
      econdaObject = { previousPage: '', currentPage: 'root page' };
    }
    econdaClickEvent(pathname, 'return button', `return to ${econdaObject.previousPage}`, 'click');
  };

  const cancelModal = () => {
    if (modalOpen) {
      econdaClickEvent(pathname, 'return button', 'return to root', 'cancel');
    }
    setModalOpen(false);
  };

  const handleOk = () => {
    econdaClickEvent(pathname, 'return button', 'return to root', 'click');
    setModalOpen(false);
    if (linkRef.current) {
      linkRef.current.click();
    }
  };

  return {
    linkRef,
    isFirstPage,
    isResultPage,
    headlineName,
    modalOpen,
    resetApplication,
    goBack,
    handleOk,
    cancelModal,
  };
};

export default useHeadline;